import { TokenLabel } from "@/components/brand/TokenAmount";
import { PageHero } from "@/components/ui/PageHero";
import { useAuth } from "@/lib/auth";
import { copyToClipboard } from "@/lib/clipboard";
import { ROUTES } from "@/lib/routes";
import { formatAmountPlain, shortPrincipal } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useSavingsBalance, useSavingsRateApr } from "@/hooks/useGovernance";
import { useCancelOrder, useMyMmkOrders } from "@/hooks/useMmkBook";
import { getMmkBookCanisterId } from "@/lib/mmk-book-actor";
import { getEnvMap } from "@/lib/network";
import { ArrowDownCircle, BookOpen, Copy, Send } from "lucide-react";
import { toast } from "sonner";
import { WalletActivityPanel } from "@/components/wallet/WalletActivityPanel";
import { WalletAssetSheet } from "@/components/wallet/WalletAssetSheet";
import { useCallback, useState } from "react";
import type { ExtendedTokenId } from "@/lib/token-assets";

export default function WalletPage() {
	const {
		isAuthenticated,
		identity,
		balances,
		balancesLive,
		balancesLoading,
		balancesError,
		principal,
		refreshBalances,
		reconnectProtocol,
		configError,
		actor,
	} = useAuth();
	const { data: savings = 0 } = useSavingsBalance();
	const { data: savingsApr } = useSavingsRateApr();
	const bookId = getMmkBookCanisterId(getEnvMap());
	const myOrdersQ = useMyMmkOrders(identity);
	const cancelMut = useCancelOrder(identity);
	const openBookOrders = (myOrdersQ.data ?? []).filter((o) => o.status === "open");
	const [sheet, setSheet] = useState<{
		mode: "deposit" | "send";
		token: ExtendedTokenId;
	} | null>(null);

	const rows = [
		["USDM", balances.USDM, 2],
		["ICP", balances.ICP, 2],
		["ckBTC", balances.ckBTC, 4],
		["ckETH", balances.ckETH, 2],
		["ckUSDC", balances.ckUSDC ?? 0, 2],
		["MMK", balances.MMK, 0],
	] as const;

	const copyPrincipal = useCallback(async () => {
		if (!principal) {
			toast.error("Connect to reveal your principal first");
			return;
		}
		const ok = await copyToClipboard(principal);
		if (ok) toast.success("Principal copied");
		else toast.error("Copy failed — select the principal and copy manually");
	}, [principal]);

	const balFor = (sym: string) => {
		if (sym === "USDM") return balances.USDM;
		if (sym === "ICP") return balances.ICP;
		if (sym === "ckBTC") return balances.ckBTC;
		if (sym === "ckETH") return balances.ckETH;
		if (sym === "ckUSDC") return balances.ckUSDC ?? 0;
		return balances.MMK;
	};

	return (
		<div className="pb-16">
			<PageHero
				eyebrow="Wallet"
				title="Balances"
				subtitle={
					isAuthenticated
						? "Your identity, holdings, savings, and activity."
						: "Connect to manage assets."
				}
			/>
			<div className="container mx-auto max-w-2xl space-y-4 px-4">
				{/* Holdings: II first row, then tokens */}
				<div className="glass-panel p-4 sm:p-5">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<p className="section-eyebrow">Holdings</p>
						{isAuthenticated ? (
							<button
								type="button"
								className="text-[11px] font-semibold text-primary"
								onClick={() => void refreshBalances()}
								disabled={balancesLoading}
							>
								{balancesLoading
									? "Refreshing…"
									: balancesLive
										? "Live · Refresh"
										: configError
											? "Unavailable"
											: "Refresh"}
							</button>
						) : null}
					</div>
					{balancesError ? (
						<p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
							{balancesError}
						</p>
					) : null}

					<ul className="mt-3 divide-y divide-border/50">
						{/* Internet Identity as first holdings row */}
						<li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
							<div className="min-w-0">
								<p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
									Internet Identity
								</p>
								{isAuthenticated && principal ? (
									<p className="mt-1 break-all rounded-xl bg-muted px-3 py-2.5 font-mono text-xs sm:text-sm">
										<span className="sm:hidden">
											{shortPrincipal(principal)}
										</span>
										<span className="hidden sm:inline">{principal}</span>
									</p>
								) : (
									<p className="mt-0.5 text-sm text-muted-foreground">
										Not connected
									</p>
								)}
							</div>
							<div className="flex flex-wrap gap-2">
								{isAuthenticated && principal ? (
									<>
										<button
											type="button"
											className="inline-flex min-h-10 flex-1 items-center justify-center gap-1.5 rounded-xl border border-border px-3 text-xs font-bold sm:flex-none"
											onClick={() => void copyPrincipal()}
										>
											<Copy className="size-3.5" aria-hidden />
											Copy
										</button>
										{!actor ? (
											<button
												type="button"
												className="inline-flex min-h-10 flex-1 items-center justify-center rounded-xl border border-amber-500/50 px-3 text-xs font-bold text-amber-700 sm:flex-none dark:text-amber-300"
												onClick={() => void reconnectProtocol()}
											>
												Reconnect
											</button>
										) : null}
									</>
								) : null}
							</div>
						</li>

						{rows.map(([sym, amt, dec]) => (
							<li
								key={sym}
								className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
							>
								<div className="flex min-w-0 items-center gap-3">
									<TokenLabel id={sym} size="md" />
									{isAuthenticated ? (
										<span className="balance-chip">
											{amt.toLocaleString(undefined, {
												minimumFractionDigits: Math.min(2, dec),
												maximumFractionDigits: dec,
											})}
										</span>
									) : (
										<span className="balance-chip opacity-50">—</span>
									)}
								</div>
								<div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0">
									<button
										type="button"
										disabled={!isAuthenticated}
										className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-border px-3 text-xs font-bold hover:border-primary/50 hover:text-primary disabled:opacity-40"
										onClick={() =>
											setSheet({ mode: "deposit", token: sym })
										}
									>
										<ArrowDownCircle className="size-3.5" aria-hidden />
										Deposit
									</button>
									<button
										type="button"
										disabled={!isAuthenticated}
										className="inline-flex min-h-10 items-center justify-center gap-1.5 rounded-xl border border-primary/40 bg-primary/10 px-3 text-xs font-bold text-primary hover:bg-primary/15 disabled:opacity-40"
										onClick={() => setSheet({ mode: "send", token: sym })}
									>
										<Send className="size-3.5" aria-hidden />
										Send
									</button>
								</div>
							</li>
						))}
					</ul>
				</div>

				{/* USDM Savings */}
				<div className="glass-panel flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5">
					<div>
						<p className="section-eyebrow">USDM Savings</p>
						<div className="mt-2 flex items-center gap-2">
							<TokenLabel id="USDM" size="md" />
							<p className="font-mono-amount text-xl font-bold text-primary">
								{isAuthenticated
									? savings.toLocaleString(undefined, {
											maximumFractionDigits: 4,
										})
									: "—"}
							</p>
						</div>
						{savingsApr != null ? (
							<p className="mt-1 text-xs text-muted-foreground">
								DSR {savingsApr.toFixed(2)}% APR · surplus-capped
							</p>
						) : (
							<p className="mt-1 text-xs text-muted-foreground">
								Earn from protocol surplus
							</p>
						)}
					</div>
					<Link
						to={ROUTES.savings}
						className="btn-primary inline-flex min-h-10 w-full items-center justify-center px-4 text-sm sm:w-auto"
					>
						Open Savings
					</Link>
				</div>

				{/* MMK secondary book */}
				<div className="glass-panel space-y-3 p-4 sm:p-5">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<div>
							<p className="section-eyebrow">MMK Book</p>
							<p className="mt-1 text-xs text-muted-foreground">
								Secondary MMK/USDM limits · wallet USDM{" "}
								{formatAmountPlain(balances.USDM ?? 0, 2)} · MMK{" "}
								{formatAmountPlain(balances.MMK ?? 0, 2)}
							</p>
						</div>
						<Link
							to={ROUTES.mmkBook}
							className="inline-flex min-h-10 items-center gap-1.5 rounded-xl border border-border px-3 text-xs font-bold"
						>
							<BookOpen className="size-3.5" aria-hidden />
							Open book
						</Link>
					</div>
					{!bookId ? (
						<p className="text-xs text-muted-foreground">
							Book canister not configured in this build.
						</p>
					) : !isAuthenticated ? (
						<p className="text-xs text-muted-foreground">
							Connect to see open limit orders.
						</p>
					) : openBookOrders.length === 0 ? (
						<p className="text-xs text-muted-foreground">No open limit orders.</p>
					) : (
						<ul className="space-y-2 text-sm">
							{openBookOrders.map((o) => (
								<li
									key={o.id}
									className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/50 px-3 py-2"
								>
									<span className="font-mono text-xs">
										#{o.id} {o.side} @ {formatAmountPlain(o.price, 4)} ×{" "}
										{formatAmountPlain(o.size, 4)}
									</span>
									<button
										type="button"
										className="text-xs font-semibold text-rose-500 underline"
										disabled={cancelMut.isPending}
										onClick={() => {
											cancelMut.mutate(o.id, {
												onSuccess: () => toast.success("Order cancelled"),
												onError: (e) =>
													toast.error(
														e instanceof Error ? e.message : "Cancel failed",
													),
											});
										}}
									>
										Cancel
									</button>
								</li>
							))}
						</ul>
					)}
				</div>

				<WalletActivityPanel />
			</div>

			{sheet ? (
				<WalletAssetSheet
					mode={sheet.mode}
					token={sheet.token}
					open
					onOpenChange={(o) => {
						if (!o) setSheet(null);
					}}
					balance={balFor(sheet.token)}
				/>
			) : null}
		</div>
	);
}
